import React from 'react';
import moment from 'moment';
import { Alert, Box, Typography, Stack, Button, Chip } from '@mui/material';
import { useFotbollPlayNotificationConf } from '../../../../../hooks/useFotbollPlayNotifications';
import { useSegments } from '../../../../../hooks/useSegments';
import PushNotificationPopup, {
  PushNotificationFormPayload,
} from '../../../../common/push-notification/push-notification-form';
import PushNotificationColumn from '../../../../common/push-notification/push-notification-column';
import { VideoItem } from '../VideoCarousel/VideoCarouselModal';
import { getVideoUrl } from '../../utils/video-utils';

const { REACT_APP_FOTBOLLPLAY_BASE_URL } = process.env;

interface NotificationSearchResultItemProps {
  teamAbbrv: string;
  item: VideoItem;
  onMoreInfo: (video: VideoItem) => void;
  onCreateDeeplink: (video: VideoItem) => void;
}

function getVideoDate(item: VideoItem): string | null {
  if (!item.date) return null;

  const date = new Date(item.date as string | number | Date);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString('sv-SE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function getVideoNotificationUrl(item: VideoItem): string {
  const webviewUrl = (item['webviewUrl'] as string) || '';
  if (webviewUrl) return webviewUrl;

  const baseUrl = (REACT_APP_FOTBOLLPLAY_BASE_URL || '').replace(/\/$/, '');
  if (baseUrl && item.id) {
    return `${baseUrl}/playlist/${item.id}`;
  }

  return getVideoUrl(item);
}

export function NotificationSearchResultItem({
  teamAbbrv,
  item,
  onMoreInfo,
  onCreateDeeplink,
}: NotificationSearchResultItemProps) {
  const videoDate = getVideoDate(item);
  const [openPushNotificationPopup, setOpenPushNotificationPopup] = React.useState(false);
  const { query, createMutation, updateMutation, deleteMutation } = useFotbollPlayNotificationConf({
    abbrv: teamAbbrv,
    id: item.id,
  });
  const { query: segmentQuery } = useSegments(teamAbbrv);
  const isLoading = query.isLoading || segmentQuery.isLoading;
  const fotbollPlayNotificationConf = query.data;
  const pushNotificationId = fotbollPlayNotificationConf?.id;
  const videoNotificationUrl = getVideoNotificationUrl(item);
  const notificationDescription = (item['description'] as string) || item.title || '';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Box
        component="img"
        src={item.thumbnail}
        alt={item.title}
        sx={{
          width: 80,
          height: 45,
          objectFit: 'cover',
          borderRadius: 1,
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {item.title}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {item.duration}
          </Typography>
          {videoDate && (
            <Typography variant="caption" color="text.secondary">
              {videoDate}
            </Typography>
          )}
          {item.channelName && (
            <Chip label={item.channelName} size="small" sx={{ height: 18, fontSize: 10 }} />
          )}
        </Stack>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" size="small" onClick={() => onMoreInfo(item)}>
          Mer info
        </Button>
        <Button variant="outlined" size="small" onClick={() => onCreateDeeplink(item)}>
          Skapa djuplänk
        </Button>
        {isLoading ? (
          <Typography variant="caption" color="text.secondary">
            Laddar...
          </Typography>
        ) : (
          <Box sx={{ minWidth: 150 }}>
            <PushNotificationColumn
              pushNotification={fotbollPlayNotificationConf}
              onClick={() => setOpenPushNotificationPopup(true)}
              abbrv={teamAbbrv}
            />
          </Box>
        )}
      </Stack>
      {openPushNotificationPopup ? (
        <PushNotificationPopup
          abbrv={teamAbbrv}
          topicContentType="FOTBOLLPLAY"
          itemInfo={
            <Alert icon={false} severity="info" color="info" variant="standard">
              <Typography variant="h6">{item.title}</Typography>
            </Alert>
          }
          content={{
            target: fotbollPlayNotificationConf?.notification?.target,
            scheduledTime: fotbollPlayNotificationConf?.scheduledTime
              ? moment(fotbollPlayNotificationConf.scheduledTime).format('YYYY-MM-DD[T]HH:mm')
              : undefined,
            title: fotbollPlayNotificationConf?.notification?.title || item.title || '',
            description:
              fotbollPlayNotificationConf?.notification?.description || notificationDescription,
          }}
          segments={segmentQuery.data || []}
          pushNotificationIsSend={fotbollPlayNotificationConf?.status === 'SENT'}
          onCreate={async (payload: PushNotificationFormPayload) => {
            const { scheduledTime, target, title, description } = payload;
            const res = await createMutation.mutateAsync({
              scheduledTime,
              notification: {
                title,
                description,
                image: item.thumbnail,
                videoUrl: videoNotificationUrl,
                target,
              },
            });
            query.refetch();
            return res;
          }}
          onUpdate={(payload: PushNotificationFormPayload) => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const { scheduledTime, target, title, description } = payload;
            return updateMutation.mutateAsync({
              id: pushNotificationId,
              scheduledTime,
              notification: {
                title,
                description,
                image: item.thumbnail,
                videoUrl: videoNotificationUrl,
                target,
              },
              createdAt: (fotbollPlayNotificationConf?.createdAt || moment().format()) as string,
            });
          }}
          onDelete={async () => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const res = await deleteMutation.mutateAsync({
              id: pushNotificationId,
              videoId: item.id,
            });
            query.refetch();
            return res;
          }}
          onClose={() => setOpenPushNotificationPopup(false)}
          isPending={
            createMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending ||
            query.isLoading
          }
          isNew={!pushNotificationId}
        />
      ) : null}
    </Box>
  );
}
